import React, { useState, useMemo } from 'react';
import { CalendarProps, CalendarCellProps, AvailabilityDate, getAvailabilityStatus, datesEqual, resolveMaxBookingsForDate } from '../../types';
import Icon from './Icon';
import './Calendar.css';

// Calendar cell component for individual dates
const CalendarCell: React.FC<CalendarCellProps> = ({
  date,
  isSelected,
  isToday,
  isCurrentMonth,
  availability,
  availabilitySettings,
  isSelectable = true,
  onClick,
  className = '',
  showBookingCount = false
}) => {
  const getStatusClass = (): string => {
    if (availability) {
      const status = getAvailabilityStatus(availability, new Date(), availabilitySettings);
      if (status === 'full' || status === 'blocked' || status === 'past') {
        return `calendar-cell--${status}`;
      }
    }

    if (!isSelectable) {
      return 'calendar-cell--disabled';
    }
    
    if (!availability) {
      // No availability record means available by default
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cellDate = new Date(date);
      cellDate.setHours(0, 0, 0, 0);
      
      if (cellDate < today) {
        return 'calendar-cell--past';
      }
      return 'calendar-cell--available';
    }
    
    const status = getAvailabilityStatus(availability, new Date(), availabilitySettings);
    return `calendar-cell--${status}`;
  };

  const getStatusIndicator = (): React.ReactNode => {
    if (availability) {
      const status = getAvailabilityStatus(availability, new Date(), availabilitySettings);
      if (status === 'full' || status === 'blocked' || status === 'past') {
        return <div className={`calendar-cell__indicator calendar-cell__indicator--${status}`} />;
      }
    }

    if (!isSelectable) {
      return <div className="calendar-cell__indicator calendar-cell__indicator--disabled" />;
    }
    
    if (!availability) {
      // No availability record means available by default
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cellDate = new Date(date);
      cellDate.setHours(0, 0, 0, 0);
      
      if (cellDate < today) {
        return <div className="calendar-cell__indicator calendar-cell__indicator--past" />;
      }
      return <div className="calendar-cell__indicator calendar-cell__indicator--available" />;
    }
    
    const status = getAvailabilityStatus(availability, new Date(), availabilitySettings);
    
    switch (status) {
      case 'available':
        return <div className="calendar-cell__indicator calendar-cell__indicator--available" />;
      case 'limited':
        return <div className="calendar-cell__indicator calendar-cell__indicator--limited" />;
      case 'full':
        return <div className="calendar-cell__indicator calendar-cell__indicator--full" />;
      case 'blocked':
        return <div className="calendar-cell__indicator calendar-cell__indicator--blocked" />;
      case 'past':
        return <div className="calendar-cell__indicator calendar-cell__indicator--past" />;
      default:
        return null;
    }
  };

  const cellClasses = [
    'calendar-cell',
    getStatusClass(),
    isSelected ? 'calendar-cell--selected' : '',
    isToday ? 'calendar-cell--today' : '',
    !isCurrentMonth ? 'calendar-cell--other-month' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      className={cellClasses}
      onClick={() => isSelectable && onClick(date)}
      disabled={!isCurrentMonth || !isSelectable}
      type="button"
    >
      <span className="calendar-cell__date">{date.getDate()}</span>
      {getStatusIndicator()}
      {showBookingCount && availability && availability.currentBookings > 0 && (
        <span className="calendar-cell__booking-count">
          {availability.currentBookings}
        </span>
      )}
    </button>
  );
};

// Main Calendar component
const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateSelect,
  onDateClick,
  availabilityDates = [],
  availabilitySettings,
  viewMode = 'month',
  className = '',
  disabled = false,
  currentMonth,
  onMonthChange,
  adminMode = false
}) => {
  const [currentDate, setCurrentDate] = useState(currentMonth || new Date());

  // Sync with external currentMonth prop
  React.useEffect(() => {
    if (currentMonth) {
      setCurrentDate(currentMonth);
    }
  }, [currentMonth]);

  // Get the first day of the current month
  const firstDayOfMonth = useMemo(() => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    return date;
  }, [currentDate]);

  // Get the last day of the current month
  const lastDayOfMonth = useMemo(() => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    return date;
  }, [currentDate]);

  // Get the first day of the calendar grid (including previous month's days)
  const firstDayOfCalendar = useMemo(() => {
    const date = new Date(firstDayOfMonth);
    const dayOfWeek = date.getDay();
    date.setDate(date.getDate() - dayOfWeek);
    return date;
  }, [firstDayOfMonth]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days = [];
    const date = new Date(firstDayOfCalendar);
    
    // Generate 42 days (6 weeks) to fill the calendar grid
    for (let i = 0; i < 42; i++) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    
    return days;
  }, [firstDayOfCalendar]);

  // Get availability for a specific date
  const getAvailabilityForDate = (date: Date): AvailabilityDate | undefined => {
    const availability = availabilityDates.find(avail => 
      datesEqual(avail.date, date)
    );
    
    // Debug logging for blocked dates
    if (availability && availability.isBlocked) {
      console.log('Found blocked date:', date.toLocaleDateString(), availability);
    }
    
    return availability;
  };

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return datesEqual(date, today);
  };

  // Check if a date is selected
  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return datesEqual(date, selectedDate);
  };

  // Check if a date is in the current month
  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Check if a date is selectable based on availability settings
  const isDateSelectable = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cellDate = new Date(date);
    cellDate.setHours(0, 0, 0, 0);
    
    // Can't select past dates
    if (cellDate < today) {
      return false;
    }
    
    const availability = getAvailabilityForDate(date);

    // In admin mode, all future dates stay clickable so blocked dates can be toggled
    if (adminMode) {
      return true;
    }

    // Check advance booking days restriction (minimum lead time)
    if (availabilitySettings?.advanceBookingDays) {
      const minAdvanceDate = new Date(today);
      minAdvanceDate.setDate(today.getDate() + availabilitySettings.advanceBookingDays);
      if (cellDate < minAdvanceDate) {
        return false; // Date is too soon (before minimum lead time)
      }
    }
    
    // Check if date is blocked (only dates in availability collection with isBlocked: true)
    if (availability && availability.isBlocked) {
      return false; // Date is explicitly blocked by admin
    }
    
    const maxBookings = resolveMaxBookingsForDate(date, availabilitySettings, availability);
    const currentBookings = availability?.currentBookings ?? 0;
    if (maxBookings != null && currentBookings >= maxBookings) {
      return false;
    }
    
    // All dates are available by default unless explicitly blocked
    return true;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (disabled) return;
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    setCurrentDate(newDate);
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (disabled) return;
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    setCurrentDate(newDate);
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  // Navigate to today
  const goToToday = () => {
    if (disabled) return;
    const today = new Date();
    setCurrentDate(today);
    if (onMonthChange) {
      onMonthChange(today);
    }
  };

  // Handle date click
  const handleDateClick = (date: Date) => {
    if (disabled) return;
    
    if (onDateClick) {
      onDateClick(date);
    } else {
      onDateSelect(date);
    }
  };

  // Format month and year for display
  const monthYearFormat = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric'
  });

  // Day names for the calendar header
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarClasses = [
    'calendar',
    `calendar--${viewMode}`,
    disabled ? 'calendar--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={calendarClasses}>
      {/* Calendar Header */}
      <div className="calendar__header">
        <div className="calendar__navigation">
          <button
            className="calendar__nav-button"
            onClick={goToPreviousMonth}
            disabled={disabled}
            type="button"
            aria-label="Previous month"
          >
            <Icon type="arrow-left" size="medium" />
          </button>
          
          <div className="calendar__month-year">
            <h3 className="calendar__title">
              {monthYearFormat.format(currentDate)}
            </h3>
            <button
              className="calendar__today-button"
              onClick={goToToday}
              disabled={disabled}
              type="button"
            >
              Today
            </button>
          </div>
          
          <button
            className="calendar__nav-button calendar__nav-button--next"
            onClick={goToNextMonth}
            disabled={disabled}
            type="button"
            aria-label="Next month"
          >
            <Icon type="arrow-right" size="medium" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar__grid">
        {/* Day names header */}
        <div className="calendar__day-names">
          {dayNames.map(dayName => (
            <div key={dayName} className="calendar__day-name">
              {dayName}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="calendar__days">
          {calendarDays.map((date, index) => (
            <CalendarCell
              key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${index}`}
              date={date}
              isSelected={isSelected(date)}
              isToday={isToday(date)}
              isCurrentMonth={isCurrentMonth(date)}
              availability={getAvailabilityForDate(date)}
              availabilitySettings={availabilitySettings}
              isSelectable={isDateSelectable(date)}
              onClick={handleDateClick}
              showBookingCount={adminMode}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="calendar__legend">
        <div className="calendar__legend-item">
          <div className="calendar__legend-indicator calendar__legend-indicator--available" />
          <span>Available</span>
        </div>
        <div className="calendar__legend-item">
          <div className="calendar__legend-indicator calendar__legend-indicator--limited" />
          <span>Limited</span>
        </div>
        <div className="calendar__legend-item">
          <div className="calendar__legend-indicator calendar__legend-indicator--full" />
          <span>Full</span>
        </div>
        <div className="calendar__legend-item">
          <div className="calendar__legend-indicator calendar__legend-indicator--blocked" />
          <span>Blocked</span>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
